package org.teddapp.project.ui.finance

import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddMenuEntry
import org.teddapp.project.ui.TeddMenu

import org.teddapp.project.ui.TeddCard

import org.teddapp.project.ui.TeddSize

import org.teddapp.project.ui.TeddType

import org.teddapp.project.ui.TeddSpace

import org.teddapp.project.ui.TeddAmountSize

import org.teddapp.project.ui.TeddAmount

import org.teddapp.project.ui.TeddTransactionRow

import org.teddapp.project.ui.LocalTeddFonts

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Expense_list
import com.tedd.db.Expense_list_item
import com.tedd.db.Finance_account
import com.tedd.db.Finance_category
import com.tedd.db.Finance_transaction
import org.teddapp.project.db.repo.LoanTx
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddRing
import org.teddapp.project.ui.TeddTokens

// ─────────────────────────────────────────────────────────────────────
// Transactions tab
// ─────────────────────────────────────────────────────────────────────

/**
 * Avertissement (point 4) affiché en haut des transactions quand le solde du compte
 * atteint un seuil fixé : limite basse (rouge) ou seuil positif/objectif (vert).
 */
@Composable
private fun TxThresholdAlert(account: Finance_account, hPad: androidx.compose.ui.unit.Dp) {
    val low = account.low_threshold
    val high = account.high_threshold
    val lowHit = low != null && account.balance <= low
    val highHit = high != null && account.balance >= high
    if (!lowHit && !highHit) return
    Column(
        modifier = Modifier.fillMaxWidth().padding(start = hPad, end = hPad, top = 4.dp, bottom = 2.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        if (highHit && high != null) {
            ThresholdAlertRow(
                icon = "check",
                accent = TeddTokens.Success,
                title = "Objectif atteint",
                detail = "Le solde a atteint le seuil de ${fmtMoney(high, account.currency, compact = true)}.",
            )
        }
        if (lowHit && low != null) {
            ThresholdAlertRow(
                icon = "flag",
                accent = TeddTokens.Danger,
                title = "Limite atteinte",
                detail = "Le solde est descendu à la limite de ${fmtMoney(low, account.currency, compact = true)}.",
            )
        }
    }
}

@Composable
private fun ThresholdAlertRow(icon: String, accent: Color, title: String, detail: String) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(accent.copy(alpha = 0.10f), RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, accent.copy(alpha = 0.35f), RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp, vertical = 9.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(9.dp),
    ) {
        Box(
            modifier = Modifier.size(26.dp).clip(RoundedCornerShape(TeddTokens.RSm)).background(accent.copy(alpha = 0.18f), RoundedCornerShape(TeddTokens.RSm)),
            contentAlignment = Alignment.Center,
        ) { TeddIcon(name = icon, size = 13.dp, color = accent, strokeWidth = 2.2.dp) }
        Column(modifier = Modifier.weight(1f)) {
            Text(title, color = accent, fontSize = 12.5.sp, fontWeight = FontWeight.SemiBold)
            Text(detail, color = TeddTokens.InkMuted, fontSize = 11.5.sp)
        }
    }
}

@Composable
internal fun TransactionsTab(
    txns: List<Finance_transaction>,
    categories: List<Finance_category>,
    accounts: List<Finance_account>,
    currentAccountId: String?,
    filter: TxFilter,
    onFilterChange: (TxFilter) -> Unit,
    period: TxPeriod,
    onPeriodChange: (TxPeriod) -> Unit,
    categoryId: String?,
    onCategoryChange: (String?) -> Unit,
    search: String,
    onSearchChange: (String) -> Unit,
    /** En mode compact (phone) : padding réduit, SummaryStrip masqué, chips scrollables. */
    compact: Boolean = false,
    /** Si non-null et compact, affiche un bouton "Voir plus" en fin de liste. */
    onSeeAll: (() -> Unit)? = null,
    /** Clic sur une transaction → ouverture en édition. */
    onEditTx: ((Finance_transaction) -> Unit)? = null,
) {
    val hPad = if (compact) 16.dp else 28.dp
    val bounds = remember(period) {
        periodBounds(period, org.teddapp.project.db.TODAY_ISO)
    }
    val filtered = remember(txns, filter, search, period, categoryId, bounds) {
        val q = search.trim().lowercase()
        txns
            .asSequence()
            .filter { it.is_deleted == 0L }
            .filter { if (filter == TxFilter.All) true else it.type == filter.key }
            .filter { bounds == null || (it.date >= bounds.first && it.date <= bounds.second) }
            .filter { categoryId == null || it.cat_id == categoryId }
            .filter { q.isEmpty() || it.description?.lowercase()?.contains(q) == true }
            .sortedWith(compareByDescending<Finance_transaction> { it.date }.thenByDescending { it.id })
            .toList()
    }

    val currency = accounts.firstOrNull { it.id == currentAccountId }?.currency ?: "XOF"
    val summary = remember(filtered, currentAccountId, categories) {
        computeSummary(filtered, currentAccountId, categories)
    }

    val groups = remember(filtered) {
        val m = linkedMapOf<String, MutableList<Finance_transaction>>()
        for (t in filtered) m.getOrPut(t.date) { mutableListOf() }.add(t)
        m
    }

    Column(modifier = Modifier.fillMaxSize()) {
        // Period chips (scrollable horizontal en compact)
        PeriodChips(
            current = period,
            onChange = onPeriodChange,
            categories = categories,
            categoryId = categoryId,
            onCategoryChange = onCategoryChange,
            compact = compact,
        )
        // Summary strip — masqué en compact (déjà dans le hero phone)
        if (!compact) {
            SummaryStrip(summary = summary, currency = currency)
        }

        // Avertissement seuil atteint (point 4) — visible dans les transactions.
        accounts.firstOrNull { it.id == currentAccountId }?.let { acc ->
            TxThresholdAlert(account = acc, hPad = hPad)
        }

        // Filters bar — recherche + segmented (segmented scrollable en compact)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = hPad, end = hPad, top = if (compact) 8.dp else 14.dp, bottom = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            // Search
            Row(
                modifier = Modifier
                    .weight(1f)
                    .widthIn(max = 280.dp)
                    .height(if (compact) 30.dp else 32.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                TeddIcon(name = "search", size = 12.dp, color = TeddTokens.InkMuted)
                BasicTextField(
                    value = search,
                    onValueChange = onSearchChange,
                    modifier = Modifier.weight(1f),
                    singleLine = true,
                    textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 12.5.sp),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    decorationBox = { inner ->
                        if (search.isEmpty()) Text("Rechercher…", color = TeddTokens.InkMuted, fontSize = 12.5.sp)
                        inner()
                    },
                )
            }
            if (!compact) Spacer(modifier = Modifier.weight(1f))
            // Segmented filter — labels courts en compact pour tenir
            Row(
                modifier = Modifier
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(3.dp),
                horizontalArrangement = Arrangement.spacedBy(0.dp),
            ) {
                TxFilter.entries.forEach { f ->
                    val on = filter == f
                    val label = if (compact) shortTxFilterLabel(f) else f.label
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RXs))
                            .background(if (on) TeddTokens.Surface else Color.Transparent, RoundedCornerShape(TeddTokens.RXs))
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = { onFilterChange(f) },
                            )
                            .padding(horizontal = if (compact) 8.dp else 11.dp, vertical = 5.dp),
                    ) {
                        Text(
                            text = label,
                            color = if (on) TeddTokens.Ink else TeddTokens.InkMuted,
                            fontSize = if (compact) 11.5.sp else 12.sp,
                            fontWeight = FontWeight.Medium,
                        )
                    }
                }
            }
        }

        // Body — IMPORTANT : weight(1f) sur le LazyColumn / EmptyState pour
        // qu'il prenne toute la hauteur restante dans la Column parente,
        // sans quoi le layout se collapse à 0dp (rien ne s'affiche).
        if (groups.isEmpty()) {
            Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                EmptyState(
                    title = "Pas de transactions",
                    sub = "Aucune transaction ne correspond à ce filtre.",
                )
            }
        } else {
            LazyColumn(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                contentPadding = PaddingValues(start = hPad, end = hPad, top = 8.dp, bottom = 32.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp),
            ) {
                groups.entries.forEach { (day, list) ->
                    item(key = "g-$day") {
                        DayGroup(
                            day = day,
                            txns = list,
                            net = list.sumOf { txSignedAmountFor(it, currentAccountId) },
                            currency = currency,
                            categories = categories,
                            accounts = accounts,
                            currentAccountId = currentAccountId,
                            onEditTx = onEditTx,
                        )
                    }
                }
                if (compact && onSeeAll != null) {
                    item(key = "see-all") {
                        SeeAllButton(
                            count = filtered.size,
                            onClick = onSeeAll,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun SeeAllButton(count: Int, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            )
            .padding(horizontal = 14.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier
                .size(28.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RSm)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "list", size = 13.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.dp)
        }
        Column(modifier = Modifier.weight(1f)) {
            Text(
                "Voir toutes les transactions",
                color = TeddTokens.Ink,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Text(
                text = "Stats, filtres par date, recherche avancée — $count transactions visibles",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                maxLines = 1,
            )
        }
        TeddIcon(name = "chevron", size = 14.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.dp)
    }
}

/**
 * Groupe d'un jour : header léger (label + date + net) + carte unique
 * contenant toutes les transactions du jour avec séparateurs internes.
 */
@Composable
internal fun DayGroup(
    day: String,
    txns: List<Finance_transaction>,
    net: Long,
    currency: String,
    categories: List<Finance_category>,
    accounts: List<Finance_account>,
    currentAccountId: String?,
    onEditTx: ((Finance_transaction) -> Unit)? = null,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        DayHeader(day = day, net = net, currency = currency)
        Spacer(modifier = Modifier.height(6.dp))
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd)),
        ) {
            txns.forEachIndexed { i, tx ->
                if (i > 0) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(1.dp)
                            .padding(start = 56.dp)
                            .background(TeddTokens.Line),
                    )
                }
                TxnRow(
                    tx = tx,
                    categories = categories,
                    accounts = accounts,
                    currentAccountId = currentAccountId,
                    currency = currency,
                    onClick = onEditTx?.let { cb -> { cb(tx) } },
                )
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────
// Période + filtre catégorie + bandeau résumé
// ─────────────────────────────────────────────────────────────────────
private fun shortTxFilterLabel(f: TxFilter): String = when (f) {
    TxFilter.All -> "Tout"
    TxFilter.Expense -> "−"
    TxFilter.Income -> "+"
    TxFilter.Transfer -> "↔"
}

@Composable
private fun PeriodChips(
    current: TxPeriod,
    onChange: (TxPeriod) -> Unit,
    categories: List<Finance_category>,
    categoryId: String?,
    onCategoryChange: (String?) -> Unit,
    compact: Boolean = false,
) {
    val hPad = if (compact) 16.dp else 28.dp
    if (compact) {
        // Compact phone : 2 lignes (chips scrollables horizontalement + filtre catégorie en dessous)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .horizontalScroll(rememberScrollState())
                .padding(start = hPad, end = hPad, top = 10.dp, bottom = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            TxPeriod.entries.forEach { p ->
                val on = current == p
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(if (on) TeddTokens.LimeSoft else TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                        .border(
                            1.dp,
                            if (on) TeddTokens.LimeStrong else TeddTokens.Line,
                            RoundedCornerShape(TeddTokens.RSm),
                        )
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onChange(p) },
                        )
                        .padding(horizontal = 10.dp, vertical = 5.dp),
                ) {
                    Text(
                        text = p.label,
                        color = if (on) TeddTokens.LimeStrong else TeddTokens.InkMuted,
                        fontSize = 11.sp,
                        fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium,
                    )
                }
            }
            CategoryFilterMenu(
                categories = categories.filter { it.is_deleted == 0L && it.type == "expense" },
                selectedId = categoryId,
                onChange = onCategoryChange,
            )
        }
    } else {
        // Desktop : tout sur une ligne avec icône calendrier et CategoryFilterMenu à droite
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = hPad, end = hPad, top = 14.dp, bottom = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            TeddIcon(name = "calendar", size = 13.dp, color = TeddTokens.InkMuted)
            TxPeriod.entries.forEach { p ->
                val on = current == p
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(if (on) TeddTokens.LimeSoft else TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                        .border(
                            1.dp,
                            if (on) TeddTokens.LimeStrong else TeddTokens.Line,
                            RoundedCornerShape(TeddTokens.RSm),
                        )
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onChange(p) },
                        )
                        .padding(horizontal = 10.dp, vertical = 5.dp),
                ) {
                    Text(
                        text = p.label,
                        color = if (on) TeddTokens.LimeStrong else TeddTokens.InkMuted,
                        fontSize = 11.sp,
                        fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium,
                    )
                }
            }
            Spacer(modifier = Modifier.weight(1f))
            CategoryFilterMenu(
                categories = categories.filter { it.is_deleted == 0L && it.type == "expense" },
                selectedId = categoryId,
                onChange = onCategoryChange,
            )
        }
    }
}

@Composable
private fun CategoryFilterMenu(
    categories: List<Finance_category>,
    selectedId: String?,
    onChange: (String?) -> Unit,
) {
    var open by remember { mutableStateOf(false) }
    val selected = categories.firstOrNull { it.id == selectedId }
    val accent = selected?.let { parseFinHex(it.color) }
    Box {
        Row(
            modifier = Modifier
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(
                    if (selected != null) (accent ?: TeddTokens.LimeSoft).copy(alpha = 0.1f) else TeddTokens.Surface,
                    RoundedCornerShape(TeddTokens.RSm),
                )
                .border(
                    1.dp,
                    if (selected != null) (accent ?: TeddTokens.LimeStrong) else TeddTokens.Line,
                    RoundedCornerShape(TeddTokens.RSm),
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = { open = true },
                )
                .padding(horizontal = 10.dp, vertical = 5.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            TeddIcon(
                name = selected?.icon ?: "category",
                size = 11.dp,
                color = accent ?: TeddTokens.InkMuted,
            )
            Text(
                text = selected?.name ?: "Toutes catégories",
                color = if (selected != null) (accent ?: TeddTokens.Ink) else TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.Medium,
            )
            TeddIcon(name = "chevrond", size = 9.dp, color = TeddTokens.InkMuted)
        }
        TeddMenu(
            expanded = open,
            onDismiss = { open = false },
            width = 260.dp,
            entries = listOf(TeddMenuEntry("Toutes catégories", icon = "category", selected = selected == null, onClick = { onChange(null) })) +
                categories.map { c ->
                    val color = parseFinHex(c.color)
                    TeddMenuEntry(
                        c.name,
                        selected = selected?.id == c.id,
                        leading = {
                            Box(
                                modifier = Modifier
                                    .size(16.dp)
                                    .clip(RoundedCornerShape(TeddTokens.RXs))
                                    .background(color.copy(alpha = 0.18f), RoundedCornerShape(TeddTokens.RXs)),
                                contentAlignment = Alignment.Center,
                            ) { TeddIcon(name = c.icon, size = 10.dp, color = color, strokeWidth = 2.dp) }
                        },
                        onClick = { onChange(c.id) },
                    )
                },
        )
    }
}

internal data class TxSummary(
    val txCount: Int,
    val expense: Long,
    val income: Long,
    val net: Long,
    val topCategoryName: String?,
    val topCategoryColor: Color?,
    val topCategoryAmount: Long,
)

internal fun computeSummary(
    txns: List<Finance_transaction>,
    currentAccountId: String?,
    categories: List<Finance_category>,
): TxSummary {
    var expense = 0L
    var income = 0L
    val byCat = mutableMapOf<String, Long>()
    for (t in txns) {
        val signed = txSignedAmountFor(t, currentAccountId)
        if (t.type == "expense") {
            expense += t.amount
            val cid = t.cat_id
            if (cid != null) byCat[cid] = (byCat[cid] ?: 0L) + t.amount
        } else if (t.type == "income") {
            income += t.amount
        }
        // transferts neutres pour les totaux dépenses/entrées
        // mais ils comptent dans le net
    }
    var netCalc = 0L
    for (t in txns) netCalc += txSignedAmountFor(t, currentAccountId)

    val topCatEntry = byCat.maxByOrNull { it.value }
    val topCat = topCatEntry?.let { e -> categories.firstOrNull { it.id == e.key } }

    return TxSummary(
        txCount = txns.size,
        expense = expense,
        income = income,
        net = netCalc,
        topCategoryName = topCat?.name,
        topCategoryColor = topCat?.let { parseFinHex(it.color) },
        topCategoryAmount = topCatEntry?.value ?: 0L,
    )
}

@Composable
private fun SummaryStrip(summary: TxSummary, currency: String) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S8).padding(top = TeddSpace.S2),
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        SummaryTile("Dépenses", -summary.expense, currency, "${countLabel(summary.txCount)} sur la période", Modifier.weight(1f))
        SummaryTile("Entrées", summary.income, currency, null, Modifier.weight(1f), signed = true)
        SummaryTile("Net", summary.net, currency, null, Modifier.weight(1f), signed = true)
        TopCategoryTile(summary = summary, currency = currency, modifier = Modifier.weight(1.2f))
    }
}

/**
 * Un chiffre de la période. Une dépense reste à l'encre, précédée du signe moins ;
 * seule une entrée prend la couleur de succès, avec son signe plus.
 */
@Composable
private fun SummaryTile(
    label: String,
    value: Long,
    currency: String,
    sub: String?,
    modifier: Modifier = Modifier,
    signed: Boolean = false,
) {
    TeddCard(modifier = modifier, shape = RoundedCornerShape(TeddTokens.RMd), padding = PaddingValues(TeddSpace.S4)) {
        Text(label, color = TeddTokens.InkMuted, style = TeddType.Caption)
        Spacer(modifier = Modifier.height(TeddSpace.S1))
        TeddAmount(value, currency = currencyLabel(currency), signed = signed)
        if (sub != null) Text(sub, color = TeddTokens.InkMuted, style = TeddType.Caption)
    }
}

@Composable
private fun TopCategoryTile(summary: TxSummary, currency: String, modifier: Modifier = Modifier) {
    val color = summary.topCategoryColor ?: TeddTokens.InkMuted
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp, vertical = 8.dp),
    ) {
        Text("TOP CATÉGORIE", color = TeddTokens.InkMuted, fontSize = 10.sp, fontWeight = FontWeight.Bold, letterSpacing = 0.5.sp)
        Spacer(modifier = Modifier.height(3.dp))
        if (summary.topCategoryName == null) {
            Text("—", color = TeddTokens.InkMuted, fontSize = 16.sp, fontWeight = FontWeight.SemiBold)
            Spacer(modifier = Modifier.height(1.dp))
            Text("Pas de dépense", color = TeddTokens.InkMuted, fontSize = 10.sp)
        } else {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                Box(
                    modifier = Modifier
                        .size(18.dp)
                        .clip(RoundedCornerShape(TeddTokens.RXs))
                        .background(color.copy(alpha = 0.18f), RoundedCornerShape(TeddTokens.RXs)),
                    contentAlignment = Alignment.Center,
                ) {
                    Box(modifier = Modifier.size(8.dp).background(color, RoundedCornerShape(2.dp)))
                }
                Text(
                    summary.topCategoryName,
                    color = TeddTokens.Ink,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
            Spacer(modifier = Modifier.height(1.dp))
            Text(
                text = "−" + fmtMoney(summary.topCategoryAmount, currency, compact = true),
                color = color,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
    }
}

private fun countLabel(n: Int): String = if (n <= 1) "$n transaction" else "$n transactions"

@Composable
private fun DayHeader(day: String, net: Long, currency: String = "XOF") {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S1),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
    ) {
        Text(dayLabel(day).uppercase(), color = TeddTokens.InkMuted, style = TeddType.Overline)
        Text(dateLabel(day), color = TeddTokens.InkMuted, style = TeddType.MonoSm)
        Spacer(modifier = Modifier.weight(1f))
        TeddAmount(net, currency = currencyLabel(currency), size = TeddAmountSize.Sm, signed = true)
    }
}

@Composable
private fun TxnRow(
    tx: Finance_transaction,
    categories: List<Finance_category>,
    accounts: List<Finance_account>,
    currentAccountId: String?,
    currency: String = "XOF",
    onClick: (() -> Unit)? = null,
) {
    val cat = tx.cat_id?.let { id -> categories.firstOrNull { it.id == id } }
    val isTransfer = tx.type == "transfer"
    val loanMeta = loanTxMeta(tx.type)
    val toAcc = if (isTransfer) accounts.firstOrNull { it.id == tx.to_account_id } else null
    TeddTransactionRow(
        label = tx.description ?: (if (isTransfer) "Vers ${toAcc?.name ?: "un autre compte"}" else loanMeta ?: cat?.name ?: "Sans libellé"),
        amount = txSignedAmountFor(tx, currentAccountId),
        meta = listOfNotNull(
            cat?.name,
            if (isTransfer) "Virement vers ${toAcc?.name ?: "un autre compte"}" else loanMeta,
        ),
        icon = when {
            isTransfer -> "arrow-right"
            loanMeta != null -> if (tx.type == LoanTx.LOAN_GIVEN || tx.type == LoanTx.REPAYMENT_PAID) "arrow-right" else "arrow-left"
            else -> cat?.icon ?: "finance"
        },
        currency = currencyLabel(currency),
        onClick = onClick,
    )
}

// ─────────────────────────────────────────────────────────────────────
// Funds tab
// ─────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────
// Lists tab
// ─────────────────────────────────────────────────────────────────────
@Composable
internal fun ListsTab(
    lists: List<Expense_list>,
    items: List<Expense_list_item>,
    categories: List<Finance_category>,
    expandedId: String?,
    onExpand: (String) -> Unit,
    currency: String,
    onRequestMarkDone: (Expense_list_item) -> Unit,
    onUntick: (String) -> Unit,
    onCreateList: () -> Unit,
    onEditList: (Expense_list) -> Unit,
    onAddItem: (listId: String, name: String, planned: Long, catId: String?, currentCount: Int) -> Unit,
    onDeleteItem: (String) -> Unit,
    onEditItem: (item: Expense_list_item, name: String, planned: Long, catId: String?) -> Unit,
    onCloneList: (Expense_list) -> Unit,
    onSendToPomodoro: (Expense_list_item) -> Unit,
    entries: List<com.tedd.db.Expense_entry> = emptyList(),
    onOpenWallet: (Expense_list_item) -> Unit = {},
) {
    val entriesByItem = remember(entries) { entries.groupBy { it.item_id } }
    val itemsByList = remember(items) { items.filter { it.is_deleted == 0L }.groupBy { it.list_id } }
    val phone = org.teddapp.project.ui.adaptive.LocalWindowClass.current == org.teddapp.project.ui.adaptive.WindowClass.Compact
    val side = if (phone) 16.dp else 28.dp
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        // En bas, la place du bouton flottant : la dernière liste ne doit pas rester dessous.
        contentPadding = PaddingValues(start = side, end = side, top = if (phone) 12.dp else 20.dp, bottom = if (phone) 96.dp else 32.dp),
        verticalArrangement = Arrangement.spacedBy(if (phone) 10.dp else 14.dp),
    ) {
        item {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Column(modifier = Modifier.weight(1f)) {
                    Text("Listes de dépenses", color = TeddTokens.Ink, style = org.teddapp.project.ui.TeddType.BodyStrong)
                    if (!phone) {
                        Text(
                            text = "Planifie avant d'acheter, coche au fur et à mesure : l'écart entre prévu et réel s'affiche.",
                            color = TeddTokens.InkMuted,
                            style = org.teddapp.project.ui.TeddType.Caption,
                        )
                    }
                }
                TeddButton(
                    text = "Nouvelle liste",
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Sm,
                    onClick = onCreateList,
                    leadingIcon = { TeddIcon(name = "plus", size = 12.dp, color = TeddTokens.InkMuted) },
                )
            }
        }
        items(lists.filter { it.is_deleted == 0L }, key = { it.id }) { l ->
            val these = itemsByList[l.id].orEmpty().sortedBy { it.order_index }
            ListCard(
                list = l,
                items = these,
                entriesByItem = entriesByItem,
                onOpenWallet = onOpenWallet,
                categories = categories,
                expanded = expandedId == l.id,
                onExpand = { onExpand(l.id) },
                currency = currency,
                onRequestMarkDone = onRequestMarkDone,
                onUntick = onUntick,
                onEditList = { onEditList(l) },
                onAddItem = { itemName, planned, catId -> onAddItem(l.id, itemName, planned, catId, these.size) },
                onDeleteItem = onDeleteItem,
                onEditItem = onEditItem,
                onCloneList = { onCloneList(l) },
                onSendToPomodoro = onSendToPomodoro,
            )
        }
    }
}

@Composable
private fun ListCard(
    list: Expense_list,
    items: List<Expense_list_item>,
    entriesByItem: Map<String, List<com.tedd.db.Expense_entry>>,
    onOpenWallet: (Expense_list_item) -> Unit,
    categories: List<Finance_category>,
    expanded: Boolean,
    onExpand: () -> Unit,
    currency: String,
    onRequestMarkDone: (Expense_list_item) -> Unit,
    onUntick: (String) -> Unit,
    onEditList: () -> Unit,
    onAddItem: (name: String, planned: Long, catId: String?) -> Unit,
    onDeleteItem: (String) -> Unit,
    onEditItem: (item: Expense_list_item, name: String, planned: Long, catId: String?) -> Unit,
    onCloneList: () -> Unit,
    onSendToPomodoro: (Expense_list_item) -> Unit,
) {
    var editingItem by remember { mutableStateOf<Expense_list_item?>(null) }
    val plannedTotal = items.sumOf { it.planned }
    val actualTotal = items.sumOf { it.actual_ ?: 0L }
    val doneCount = items.count { it.is_done == 1L }
    val pct = if (items.isEmpty()) 0 else (doneCount * 100 / items.size)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RMd))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd)),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onExpand,
                )
                .padding(horizontal = 16.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            TeddRing(
                value = pct,
                size = 42.dp,
                strokeWidth = 4.dp,
                showLabel = true,
            )
            // Le nom d'abord, sur toute la largeur utile ; la note et la date dessous, chacune sur
            // sa ligne. Côte à côte, la date se retrouvait écrite en colonne, lettre par lettre.
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    text = list.name,
                    color = TeddTokens.Ink,
                    style = org.teddapp.project.ui.TeddType.BodyStrong,
                    maxLines = 2,
                    overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis,
                )
                if (!list.description.isNullOrBlank()) {
                    Text(
                        text = list.description,
                        color = TeddTokens.InkMuted,
                        style = org.teddapp.project.ui.TeddType.Caption,
                        maxLines = 2,
                        overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis,
                    )
                }
                Text(
                    text = listOfNotNull(
                        list.date?.let { dateLabel(it) },
                        "$doneCount sur ${items.size} coché${if (doneCount > 1) "s" else ""}",
                    ).joinToString(" · "),
                    color = TeddTokens.InkMuted,
                    style = org.teddapp.project.ui.TeddType.MonoSm.copy(fontSize = 11.sp),
                    maxLines = 1,
                )
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(
                    text = money(plannedTotal, currency, compact = true),
                    color = TeddTokens.Ink,
                    style = org.teddapp.project.ui.TeddType.Amount,
                    maxLines = 1,
                )
                Text(
                    text = "réel " + money(actualTotal, currency, compact = true),
                    color = if (actualTotal > plannedTotal) TeddTokens.Danger else TeddTokens.InkMuted,
                    style = org.teddapp.project.ui.TeddType.MonoSm.copy(fontSize = 11.sp),
                    maxLines = 1,
                )
            }
            TeddIcon(name = if (expanded) "chevron-down" else "chevron-right", size = 16.dp, color = TeddTokens.InkMuted)
        }
        if (expanded) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(TeddTokens.SurfaceSunken)
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                    .padding(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 12.dp),
                verticalArrangement = Arrangement.spacedBy(2.dp),
            ) {
                items.forEach { item ->
                    val cat = item.cat_id?.let { id -> categories.firstOrNull { it.id == id } }
                    val catColor = cat?.let { parseFinHex(it.color) } ?: TeddTokens.InkSubtle
                    val done = item.is_done == 1L
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .padding(horizontal = 8.dp, vertical = 6.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        Box(
                            modifier = Modifier
                                .size(16.dp)
                                .clip(RoundedCornerShape(TeddTokens.RXs))
                                .then(
                                    if (done) Modifier.background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RXs))
                                    else Modifier.border(1.5.dp, catColor, RoundedCornerShape(TeddTokens.RXs))
                                )
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = {
                                        if (item.is_done == 1L) onUntick(item.id)
                                        else onRequestMarkDone(item)
                                    },
                                ),
                            contentAlignment = Alignment.Center,
                        ) {
                            if (done) TeddIcon(name = "check", size = 10.dp, color = TeddTokens.OnAction, strokeWidth = 3.dp)
                        }
                        TeddIcon(name = cat?.icon ?: "wallet", size = 13.dp, color = catColor)
                        Text(
                            text = item.name,
                            color = if (done) TeddTokens.InkMuted else TeddTokens.Ink,
                            fontSize = 12.5.sp,
                            modifier = Modifier.weight(1f),
                            textDecoration = if (done) TextDecoration.LineThrough else TextDecoration.None,
                            maxLines = 1,
                        )
                        Text(
                            text = "Prévu " + money(item.planned, currency, compact = true),
                            color = TeddTokens.InkMuted,
                            fontSize = 11.5.sp,
                        )
                        if (item.actual_ != null) {
                            Text(
                                text = "Réel " + money(item.actual_, currency, compact = true),
                                color = if (item.actual_ > item.planned) TeddTokens.Danger else TeddTokens.Success,
                                fontSize = 11.5.sp,
                                fontWeight = FontWeight.SemiBold,
                            )
                        }
                        // Send to Pomodoro — hidden if already paid (done)
                        if (!done) {
                            Box(
                                modifier = Modifier
                                    .size(22.dp)
                                    .clip(RoundedCornerShape(TeddTokens.RXs))
                                    .background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RXs))
                                    .clickable(
                                        interactionSource = remember { MutableInteractionSource() },
                                        indication = null,
                                        onClick = { onSendToPomodoro(item) },
                                    ),
                                contentAlignment = Alignment.Center,
                            ) {
                                TeddIcon(name = "plus", size = 11.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.4.dp)
                            }
                        }
                        // Portefeuille : ouvrir les sous-dépenses de la ligne
                        val itemEntries = entriesByItem[item.id].orEmpty()
                        Box(
                            modifier = Modifier
                                .size(22.dp)
                                .clip(RoundedCornerShape(TeddTokens.RXs))
                                .background(if (itemEntries.isNotEmpty()) TeddTokens.LimeSoft else Color.Transparent, RoundedCornerShape(TeddTokens.RXs))
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { onOpenWallet(item) },
                                ),
                            contentAlignment = Alignment.Center,
                        ) {
                            TeddIcon(name = "wallet", size = 12.dp, color = if (itemEntries.isNotEmpty()) TeddTokens.LimeStrong else TeddTokens.InkMuted, strokeWidth = 1.6.dp)
                        }
                        // Edit item
                        Box(
                            modifier = Modifier
                                .size(20.dp)
                                .clip(RoundedCornerShape(TeddTokens.RXs))
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { editingItem = item },
                                ),
                            contentAlignment = Alignment.Center,
                        ) {
                            TeddIcon(name = "edit", size = 11.dp, color = TeddTokens.InkMuted, strokeWidth = 1.6.dp)
                        }
                        // Delete item
                        Box(
                            modifier = Modifier
                                .size(20.dp)
                                .clip(RoundedCornerShape(TeddTokens.RXs))
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { onDeleteItem(item.id) },
                                ),
                            contentAlignment = Alignment.Center,
                        ) {
                            TeddIcon(name = "trash", size = 11.dp, color = TeddTokens.InkMuted, strokeWidth = 1.6.dp)
                        }
                    }
                    // Une ligne-portefeuille dit où elle en est, sous son nom.
                    entriesByItem[item.id]?.takeIf { it.isNotEmpty() }?.let { es ->
                        val spentHere = es.sumOf { it.amount }
                        val leftHere = item.planned - spentHere
                        Text(
                            text = "${es.size} sous-dépense${if (es.size > 1) "s" else ""} · " +
                                if (leftHere >= 0) "reste " + money(leftHere, currency, compact = true)
                                else "dépassé de " + money(-leftHere, currency, compact = true),
                            color = if (leftHere < 0) TeddTokens.Danger else TeddTokens.InkMuted,
                            style = org.teddapp.project.ui.TeddType.MonoSm.copy(fontSize = 11.sp),
                            modifier = Modifier
                                .padding(start = 34.dp, bottom = 4.dp)
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { onOpenWallet(item) },
                                ),
                        )
                    }
                }
                // Add-item button — ouvre un panneau dédié plutôt qu'une ligne squeezée
                Spacer(modifier = Modifier.height(4.dp))
                var showAddItem by remember { mutableStateOf(false) }
                AddItemButton(
                    listName = list.name,
                    onClick = { showAddItem = true },
                )
                if (showAddItem) {
                    AddListItemPanel(
                        listName = list.name,
                        categories = categories.filter { it.type == "expense" && it.is_deleted == 0L },
                        currency = currency,
                        onClose = { showAddItem = false },
                        onSave = { name, planned, catId ->
                            onAddItem(name, planned, catId)
                            showAddItem = false
                        },
                    )
                }
                editingItem?.let { item ->
                    AddListItemPanel(
                        listName = list.name,
                        categories = categories.filter { it.type == "expense" && it.is_deleted == 0L },
                        currency = currency,
                        existing = item,
                        onClose = { editingItem = null },
                        onSave = { name, planned, catId ->
                            onEditItem(item, name, planned, catId)
                            editingItem = null
                        },
                    )
                }
                // Edit-list footer
                Spacer(modifier = Modifier.height(8.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End)) {
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RXs))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RXs))
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = onCloneList,
                            )
                            .padding(horizontal = 10.dp, vertical = 5.dp),
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                            TeddIcon(name = "cards", size = 11.dp, color = TeddTokens.InkMuted, strokeWidth = 1.8.dp)
                            Text(text = "Dupliquer", color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.Medium)
                        }
                    }
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RXs))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RXs))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RXs))
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = onEditList,
                            )
                            .padding(horizontal = 10.dp, vertical = 5.dp),
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                            TeddIcon(name = "edit", size = 11.dp, color = TeddTokens.InkMuted, strokeWidth = 1.8.dp)
                            Text(text = "Modifier la liste", color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.Medium)
                        }
                    }
                }
            }
        }
    }
}

/**
 * Bouton "+ Ajouter un item" — déclencheur du panneau d'ajout.
 * Remplace l'ancienne ligne inline qui était trop cramped sur mobile.
 */
@Composable
private fun AddItemButton(listName: String, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.LimeStrong.copy(alpha = 0.35f), RoundedCornerShape(TeddTokens.RSm))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            )
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(
            modifier = Modifier
                .size(24.dp)
                .clip(CircleShape)
                .background(TeddTokens.LimeStrong, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "plus", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp)
        }
        Text(
            "Ajouter un item",
            color = TeddTokens.LimeStrong,
            fontSize = 12.5.sp,
            fontWeight = FontWeight.SemiBold,
        )
        Spacer(modifier = Modifier.weight(1f))
        Text(
            text = listName,
            color = TeddTokens.LimeStrong.copy(alpha = 0.65f),
            fontSize = 11.sp,
            maxLines = 1,
        )
    }
}

/**
 * Panneau "Ajouter un item à la liste" — plein écran sur Phone via
 * LocalUseFullScreenForm, Dialog centré 440dp sur Mac.
 * Nom (input large), catégories en pills, montant prévu (gros monospace), submit.
 */
@Composable
private fun AddListItemPanel(
    listName: String,
    categories: List<Finance_category>,
    currency: String,
    onClose: () -> Unit,
    onSave: (name: String, planned: Long, catId: String?) -> Unit,
    /** Élément à modifier ; null pour un ajout. */
    existing: Expense_list_item? = null,
) {
    var name by remember(existing) { mutableStateOf(existing?.name ?: "") }
    var planned by remember(existing) { mutableStateOf(existing?.planned?.takeIf { it > 0 }?.toString() ?: "") }
    var catId by remember(existing) { mutableStateOf(existing?.cat_id ?: categories.firstOrNull()?.id) }
    val plannedValue = planned.toLongOrNull() ?: 0L
    val canSave = name.isNotBlank() && plannedValue > 0L
    val currencySym = when (currency) {
        "USD" -> "$"
        "EUR" -> "€"
        else -> "F"
    }
    val fullScreen = org.teddapp.project.ui.LocalUseFullScreenForm.current

    Dialog(
        onDismissRequest = onClose,
        properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = true),
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .then(
                    if (fullScreen) Modifier.background(TeddTokens.Bg)
                    else Modifier.background(Color.Black.copy(alpha = 0.35f))
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onClose,
                ),
        ) {
            Column(
                modifier = Modifier
                    .then(
                        if (fullScreen) Modifier.fillMaxSize()
                        else Modifier.align(Alignment.Center).width(TeddSize.SidePanel).fillMaxHeight(0.86f)
                    )
                    .background(TeddTokens.Bg)
                    .then(if (fullScreen) Modifier.imePadding() else Modifier)
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = {},
                    ),
            ) {
                // Header
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(start = 16.dp, end = 12.dp, top = if (fullScreen) 56.dp else 18.dp, bottom = 14.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            "NOUVEL ITEM",
                            color = TeddTokens.InkMuted,
                            fontSize = 10.sp,
                            fontWeight = FontWeight.Bold,
                            letterSpacing = 0.6.sp,
                        )
                        Spacer(modifier = Modifier.height(2.dp))
                        Text(
                            text = listName,
                            color = TeddTokens.Ink,
                            fontSize = 17.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                    Box(
                        modifier = Modifier
                            .size(36.dp)
                            .clip(CircleShape)
                            .background(TeddTokens.Surface, CircleShape)
                            .border(1.dp, TeddTokens.Line, CircleShape)
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = onClose,
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted, strokeWidth = 1.8.dp)
                    }
                }
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = 16.dp),
                ) {
                    // Nom
                    Text("Nom de l'item", color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
                    Spacer(modifier = Modifier.height(6.dp))
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(46.dp)
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                            .padding(horizontal = 14.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Box(modifier = Modifier.weight(1f)) {
                            if (name.isEmpty()) {
                                Text("Ex. Riz brisé 5kg", color = TeddTokens.InkSubtle, fontSize = 14.sp)
                            }
                            BasicTextField(
                                value = name,
                                onValueChange = { name = it },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 14.sp),
                                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    // Montant prévu — gros monospace
                    Text("Montant prévu", color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
                    Spacer(modifier = Modifier.height(6.dp))
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(TeddTokens.RMd))
                            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                            .padding(horizontal = 16.dp, vertical = 12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Box(modifier = Modifier.weight(1f)) {
                            if (planned.isEmpty()) {
                                Text(
                                    "0",
                                    color = TeddTokens.InkSubtle,
                                    fontSize = 30.sp,
                                    fontWeight = FontWeight.Medium,
                                    fontFamily = LocalTeddFonts.current.mono,
                                )
                            }
                            BasicTextField(
                                value = planned,
                                onValueChange = { v -> if (v.all { c -> c.isDigit() }) planned = v },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                textStyle = TextStyle(
                                    color = TeddTokens.Ink,
                                    fontSize = 30.sp,
                                    fontWeight = FontWeight.Medium,
                                    fontFamily = LocalTeddFonts.current.mono,
                                    letterSpacing = (-0.6).sp,
                                ),
                                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                            )
                        }
                        Text(currencySym, color = TeddTokens.InkMuted, fontSize = 16.sp, fontWeight = FontWeight.Medium)
                    }

                    if (categories.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(16.dp))
                        Text("Catégorie", color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
                        Spacer(modifier = Modifier.height(8.dp))
                        // Catégories en grid wrap (3 colonnes via Row + Column manuel n'est pas trivial)
                        // Solution simple : scroll horizontal de pills colorées
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .horizontalScroll(rememberScrollState()),
                            horizontalArrangement = Arrangement.spacedBy(6.dp),
                        ) {
                            categories.forEach { c ->
                                val color = parseFinHex(c.color)
                                val on = c.id == catId
                                Row(
                                    modifier = Modifier
                                        .clip(RoundedCornerShape(TeddTokens.RFull))
                                        .background(
                                            if (on) color.copy(alpha = 0.14f) else TeddTokens.Surface,
                                            RoundedCornerShape(TeddTokens.RFull),
                                        )
                                        .border(
                                            1.dp,
                                            if (on) color else TeddTokens.Line,
                                            RoundedCornerShape(TeddTokens.RFull),
                                        )
                                        .clickable(
                                            interactionSource = remember { MutableInteractionSource() },
                                            indication = null,
                                            onClick = { catId = c.id },
                                        )
                                        .padding(horizontal = 12.dp, vertical = 8.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                                ) {
                                    TeddIcon(
                                        name = c.icon,
                                        size = 13.dp,
                                        color = if (on) color else TeddTokens.InkMuted,
                                        strokeWidth = 2.dp,
                                    )
                                    Text(
                                        c.name,
                                        color = if (on) color else TeddTokens.Ink,
                                        fontSize = 12.5.sp,
                                        fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium,
                                    )
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(24.dp))
                }
                // Footer — bouton submit
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                        .padding(16.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .height(46.dp)
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = onClose,
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text("Annuler", color = TeddTokens.InkMuted, fontSize = 14.sp, fontWeight = FontWeight.Medium)
                    }
                    Box(
                        modifier = Modifier
                            .weight(2f)
                            .height(46.dp)
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(
                                if (canSave) TeddTokens.SignatureGradient else SolidColor(TeddTokens.Line),
                                RoundedCornerShape(TeddTokens.RSm),
                            )
                            .clickable(
                                enabled = canSave,
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = {
                                    if (canSave) onSave(name.trim(), plannedValue, catId)
                                },
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                            TeddIcon(name = "check", size = 15.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp)
                            Text("Ajouter l'item", color = TeddTokens.OnAction, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                        }
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────
// Notes tab
// ─────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────
@Composable
private fun EmptyState(title: String, sub: String) {
    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        TeddEmptyState(title = title, message = sub, icon = "finance")
    }
}
